/**
 * Detect tool calls that read agent memory. Complements
 * `classifyMemoryWrite`. Pattern matching lives in the Rust core; this
 * file is a thin NAPI wrapper.
 */
import { native } from "../native.js";
import type { ClassifierToolContext, ClassifierToolEvent } from "./classifier.js";

/** Dedicated memory-read tool names, matched case-insensitively. */
export const DEFAULT_MEMORY_READ_TOOL_NAMES: ReadonlyArray<string> =
  native.defaultMemoryReadToolNames();

export interface ClassifyMemoryReadOptions {
  /** Tool names that always count as memory reads. Merged with defaults. */
  readToolNames?: ReadonlyArray<string>;
  /** Additional path substrings that mark a file as memory. Merged with defaults. */
  patterns?: ReadonlyArray<string>;
  /** Additional generic-read tool names whose path arg to inspect. Merged with defaults. */
  genericReadToolNames?: ReadonlyArray<string>;
}

/**
 * Returns `true` when this tool call is a memory read — either a
 * dedicated memory-read tool from `readToolNames`, or a generic read
 * tool (`read` / `read_file`) targeting a memory-shaped path.
 *
 * Caller-supplied `patterns` are MERGED with the defaults (matches
 * Node's original behavior — extending in one plugin doesn't disable
 * standard coverage).
 */
export function classifyMemoryRead(
  event: unknown,
  ctx: unknown,
  opts: ClassifyMemoryReadOptions = {},
): boolean {
  return native.classifyMemoryRead(event, ctx, {
    readToolNames: opts.readToolNames ? [...opts.readToolNames] : undefined,
    patterns: opts.patterns ? [...opts.patterns] : undefined,
    genericReadToolNames: opts.genericReadToolNames
      ? [...opts.genericReadToolNames]
      : undefined,
  });
}

// Re-export shared types so callers can `import type { ClassifierToolContext } from "./read-classifier.js"`.
export type { ClassifierToolContext, ClassifierToolEvent };
